import { useCallback, useState } from 'react'

import { DomainProbeStatus } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useNominateNewDomain,
  useProbeDomain,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { HelperText } from '@tribeplatform/react-ui-kit/HelperText'

import { RuntimeConfigs } from '../../../../../../../configs/runtime.js'
import { DomainInputHelper } from '../components/DomainInputHelper.js'

interface Props {
  canUseCustomDomain: boolean
}

export const ChooseDomain = ({ canUseCustomDomain }: Props) => {
  const { $t } = useI18n()
  const [newDomain, setNewDomain] = useState<string>(null)

  const {
    data: probeResult,
    isFetching: isProbing,
    isError: hasProbeError,
    remove: clearProbeResult,
  } = useProbeDomain({
    variables: {
      domain: newDomain,
    },
    useQueryOptions: {
      enabled: !!newDomain,
    },
  })

  const {
    mutateAsync: nominateDomain,
    isLoading: isNominating,
    isError: hasNominationError,
  } = useNominateNewDomain()

  const handleSubmit = useCallback(
    async (data: { newDomain: string }) => {
      if (probeResult?.status === DomainProbeStatus.Available) {
        try {
          await nominateDomain({ domain: newDomain })
          clearProbeResult()
        } catch (error) {
          // No-op. Handled in form using the error state.
        }
      } else {
        const domainWithoutProtocol = data.newDomain.replace(/^https?:\/\//, '')
        if (canUseCustomDomain) {
          setNewDomain(domainWithoutProtocol)
        } else {
          setNewDomain(
            `${domainWithoutProtocol}${RuntimeConfigs.VALID_NATIVE_DOMAIN}`,
          )
        }
      }
    },
    [
      canUseCustomDomain,
      newDomain,
      nominateDomain,
      clearProbeResult,
      probeResult?.status,
    ],
  )

  return (
    <Form<{ newDomain: string }> onSubmit={handleSubmit} className="mt-0">
      <Form.Input
        name="newDomain"
        label={
          canUseCustomDomain
            ? $t({
                id: 'Admin.Domain.NewDomainInput',
                defaultMessage: 'Enter your own domain',
              })
            : $t({
                id: 'Admin.Domain.NewSubdomainInput',
                defaultMessage: 'Choose a subdomain',
              })
        }
        onChange={() => setNewDomain(null)}
        className="font-mono"
        placeholder={
          canUseCustomDomain
            ? $t({
                id: 'Admin.Domain.NewDomainInput.Placeholder',
                defaultMessage: 'e.g. hub.mysite.com',
              })
            : ''
        }
        helperText={
          <DomainInputHelper
            probeResult={probeResult}
            canUseCustomDomain={canUseCustomDomain}
          />
        }
        trailingAddon={
          canUseCustomDomain ? null : RuntimeConfigs.VALID_NATIVE_DOMAIN
        }
      />
      <Form.Actions className="mt-5">
        {probeResult?.status === DomainProbeStatus.Available ? (
          <Button type="submit" loading={isNominating} variant="primary">
            <T
              defaultMessage="Start migration to <strong>{new_domain}</strong>"
              id="Admin.Domain.Nominate"
              values={{
                new_domain: newDomain,
              }}
            />
          </Button>
        ) : (
          <Button variant="primary" type="submit" loading={isProbing}>
            <T defaultMessage="Check transferability" id="Admin.Domain.Check" />
          </Button>
        )}
      </Form.Actions>
      {(hasProbeError || hasNominationError) && (
        <HelperText status="error">
          <T
            defaultMessage="Something went wrong, please try again."
            id="Generics.SomethingWentWrongTryAgain"
          />
        </HelperText>
      )}
    </Form>
  )
}
